import React, { useEffect, useRef, useState } from 'react';
import { array, func, node, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { FiPaperclip, FiX } from 'react-icons/fi';
import { partitionFilesForUpload, failedUploadMessageId } from '../../util/fileAttachments';

import css from './MessageAttachmentsPicker.css';

// Attachment staging primitives shared by every composer that can attach files
// to a message: the file-picker hook, the staged chips, the upload hints and
// the focus-keeping mousedown handler. FieldMessageComposer (enquiry and
// application-accept forms) and SendMessageForm (chat) compose them into their
// own layouts.

// mousedown handler for the picker's buttons: keep focus (and the caret) in the
// message field. Letting a button take focus blurs the field, which marks it
// touched in Final Form; a required-field error then renders and shifts the
// layout between mousedown and mouseup, so the click never lands.
export const keepComposerFocus = e => e.preventDefault();

// Staged-but-unsent files are lost on page unload, so warn before leaving.
export const useUnsavedAttachmentsWarning = hasStagedAttachments => {
  useEffect(() => {
    if (!hasStagedAttachments) {
      return undefined;
    }
    const onBeforeUnload = e => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasStagedAttachments]);
};

// Hidden file input wiring: turns a selection into uploads, enforcing the
// client-side count/size limits and exposing the (last) rejection reason.
export const useAttachmentFilePicker = ({ stagedAttachments, onUploadAttachment }) => {
  const fileInputRef = useRef(null);
  // Translation key shown when a file selection is (partially) rejected client-side.
  const [pickErrorId, setPickErrorId] = useState(null);

  const handleFilesSelected = e => {
    const files = Array.from(e.target.files || []);
    // Reset the input so picking the same file again re-triggers onChange.
    e.target.value = '';
    if (files.length === 0) {
      return;
    }
    const { accepted, errorId } = partitionFilesForUpload(files, stagedAttachments.length);
    setPickErrorId(errorId);
    accepted.forEach(file => onUploadAttachment(file));
  };

  const openFilePicker = () => fileInputRef.current && fileInputRef.current.click();
  const clearPickError = () => setPickErrorId(null);

  return { fileInputRef, pickErrorId, clearPickError, handleFilesSelected, openFilePicker };
};

// Staged (not yet sent) attachments as pills. `children` render first in the
// row, which lets a composer place its attach button at the start of the chips.
const StagedAttachmentChipsComponent = props => {
  const { className, attachments, onRemove, children, intl } = props;
  return (
    <div className={classNames(css.chips, className)}>
      {children}
      {attachments.map(attachment => {
        const isUploading = attachment.state === 'uploading';
        const isFailed = attachment.state === 'failed';
        return (
          <div
            key={attachment.tempId}
            className={classNames(css.chip, { [css.chipFailed]: isFailed })}
          >
            <FiPaperclip className={css.chipIcon} />
            <span className={css.chipName}>{attachment.name}</span>
            {isUploading ? <span className={css.chipProgress}>{attachment.progress}%</span> : null}
            {isFailed ? (
              <span className={css.chipError}>
                <FormattedMessage id={failedUploadMessageId(attachment.error)} />
              </span>
            ) : null}
            <button
              type="button"
              className={css.chipRemoveButton}
              onMouseDown={keepComposerFocus}
              onClick={() => onRemove(attachment.tempId)}
              aria-label={intl.formatMessage(
                { id: 'MessageAttachmentsPicker.removeFileAriaLabel' },
                { name: attachment.name }
              )}
            >
              <FiX />
            </button>
          </div>
        );
      })}
    </div>
  );
};

StagedAttachmentChipsComponent.defaultProps = { className: null, children: null };
StagedAttachmentChipsComponent.propTypes = {
  className: string,
  attachments: array.isRequired,
  onRemove: func.isRequired,
  children: node,
  intl: intlShape.isRequired,
};

export const StagedAttachmentChips = injectIntl(StagedAttachmentChipsComponent);

// Upload-in-progress hint and client-side selection error, shown under the chips.
export const AttachmentHints = props => {
  const { attachments, pickErrorId } = props;
  const anyUploading = attachments.some(a => a.state === 'uploading');
  return (
    <>
      {anyUploading ? (
        <p className={css.uploadingHint}>
          <FormattedMessage id="MessageAttachmentsPicker.uploadingHint" />
        </p>
      ) : null}
      {pickErrorId ? (
        <p className={css.pickError}>
          <FormattedMessage id={pickErrorId} />
        </p>
      ) : null}
    </>
  );
};

AttachmentHints.defaultProps = { pickErrorId: null };
AttachmentHints.propTypes = { attachments: array.isRequired, pickErrorId: string };
